import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  ArrowLeft,
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ImagePlus,
  Quote,
  Link as LinkIcon,
  Save,
  Undo2,
  Redo2,
} from "lucide-react";
import { toast } from "sonner";
import { sanitizeHtml } from "../lib/sanitize";
import { compressImageToBase64 } from "../lib/imageCompression";

const fileToBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

const readAsCompressed = async (file) => {
  if (file && file.type && file.type.startsWith("image/")) {
    return compressImageToBase64(file);
  }
  return fileToBase64(file);
};

const ToolButton = ({ onClick, children, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    data-testid={testId}
    className="p-2 rounded-md hover:bg-muted text-foreground/70 hover:text-foreground transition-colors"
  >
    {children}
  </button>
);

export default function BlogEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const coverRef = useRef(null);
  const [diseases, setDiseases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    category: params.get("category") || "disease",
    disease_id: params.get("disease_id") || "",
    cover_image: "",
    content: "",
    status: "published",
  });

  useEffect(() => {
    api.get("/diseases").then((r) => setDiseases(r.data.items || r.data));
  }, []);

  useEffect(() => {
    if (id) {
      api.get(`/blogs/${id}`).then((r) => {
        setForm({
          title: r.data.title || "",
          excerpt: r.data.excerpt || "",
          category: r.data.category,
          disease_id: r.data.disease_id || "",
          cover_image: r.data.cover_image || "",
          content: r.data.content || "",
          status: r.data.status || "published",
        });
        if (editorRef.current) editorRef.current.innerHTML = sanitizeHtml(r.data.content || "");
      });
    }
  }, [id]);

  // Sync initial content into editor
  useEffect(() => {
    if (!id && editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = "";
    }
  }, [id]);

  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const insertImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await readAsCompressed(file);
    editorRef.current?.focus();
    document.execCommand(
      "insertHTML",
      false,
      `<p><img src="${b64}" alt="inline" /></p><p><br/></p>`
    );
    if (fileRef.current) fileRef.current.value = "";
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await readAsCompressed(file);
      setForm((f) => ({ ...f, cover_image: b64 }));
    } catch (err) {
      console.error("cover compression failed", err);
      toast.error("Could not process cover image");
    }
  };

  const save = async () => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const content = editorRef.current?.innerHTML || "";
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        category: form.category,
        disease_id: form.category === "disease" ? form.disease_id || null : null,
        cover_image: form.cover_image || null,
        content,
        status: form.status,
      };
      if (id) {
        await api.put(`/blogs/${id}`, payload);
        toast.success("Blog updated");
      } else {
        const r = await api.post("/blogs", payload);
        toast.success("Blog published");
        navigate(`/blog/${r.data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 fade-up max-w-4xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid="blog-back-btn"
      >
        <ArrowLeft className="size-4" /> Back
      </button>

      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
            {id ? "Edit Blog" : "New Blog"}
          </p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
            {id ? "Refine your story" : "Write a new blog"}
          </h1>
        </div>
        <div className="flex gap-2 items-center">
          <Select
            value={form.status}
            onValueChange={(v) => setForm({ ...form, status: v })}
          >
            <SelectTrigger className="w-36" data-testid="blog-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={save} disabled={saving} data-testid="save-blog-btn" size="lg">
            <Save className="size-4 mr-2" /> {saving ? "Saving…" : id ? "Update" : "Save"}
          </Button>
        </div>
      </header>

      <Card className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="A meaningful title…"
              className="text-lg"
              data-testid="blog-title-input"
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger data-testid="blog-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="disease">Disease</SelectItem>
                <SelectItem value="diet">Diet</SelectItem>
                <SelectItem value="exercise">Exercise</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.category === "disease" && (
          <div>
            <Label>Tag to disease (optional)</Label>
            <Select
              value={form.disease_id || "none"}
              onValueChange={(v) => setForm({ ...form, disease_id: v === "none" ? "" : v })}
            >
              <SelectTrigger data-testid="blog-disease-select">
                <SelectValue placeholder="Select a disease" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {diseases.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label>Excerpt</Label>
          <Textarea
            rows={2}
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            placeholder="Short summary shown in listings…"
            data-testid="blog-excerpt-input"
          />
        </div>

        <div>
          <Label>Cover image</Label>
          <div className="flex items-center gap-3">
            {form.cover_image ? (
              <img
                src={form.cover_image}
                alt="cover"
                className="size-20 object-cover rounded-md border border-border"
              />
            ) : (
              <div className="size-20 grid place-items-center border border-dashed border-border rounded-md text-muted-foreground">
                <ImagePlus className="size-5" />
              </div>
            )}
            <input
              ref={coverRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={uploadCover}
              data-testid="blog-cover-input"
            />
            <Button variant="outline" onClick={() => coverRef.current?.click()}>
              {form.cover_image ? "Change" : "Upload"}
            </Button>
            {form.cover_image && (
              <Button
                variant="ghost"
                onClick={() => setForm({ ...form, cover_image: "" })}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center gap-1 flex-wrap p-2 border-b border-border bg-muted/30">
          <ToolButton onClick={() => exec("bold")} label="Bold" testId="tb-bold">
            <Bold className="size-4" />
          </ToolButton>
          <ToolButton onClick={() => exec("italic")} label="Italic" testId="tb-italic">
            <Italic className="size-4" />
          </ToolButton>
          <div className="w-px h-5 bg-border mx-1" />
          <ToolButton
            onClick={() => exec("formatBlock", "<h2>")}
            label="Heading 2"
            testId="tb-h2"
          >
            <Heading2 className="size-4" />
          </ToolButton>
          <ToolButton
            onClick={() => exec("formatBlock", "<h3>")}
            label="Heading 3"
            testId="tb-h3"
          >
            <Heading3 className="size-4" />
          </ToolButton>
          <ToolButton
            onClick={() => exec("formatBlock", "<blockquote>")}
            label="Quote"
            testId="tb-quote"
          >
            <Quote className="size-4" />
          </ToolButton>
          <div className="w-px h-5 bg-border mx-1" />
          <ToolButton
            onClick={() => exec("insertUnorderedList")}
            label="Bulleted list"
            testId="tb-ul"
          >
            <List className="size-4" />
          </ToolButton>
          <ToolButton
            onClick={() => exec("insertOrderedList")}
            label="Numbered list"
            testId="tb-ol"
          >
            <ListOrdered className="size-4" />
          </ToolButton>
          <div className="w-px h-5 bg-border mx-1" />
          <ToolButton onClick={insertLink} label="Link" testId="tb-link">
            <LinkIcon className="size-4" />
          </ToolButton>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={insertImage}
            data-testid="tb-image-input"
          />
          <ToolButton
            onClick={() => fileRef.current?.click()}
            label="Insert image"
            testId="tb-image"
          >
            <ImagePlus className="size-4" />
          </ToolButton>
          <div className="w-px h-5 bg-border mx-1" />
          <ToolButton onClick={() => exec("undo")} label="Undo" testId="tb-undo">
            <Undo2 className="size-4" />
          </ToolButton>
          <ToolButton onClick={() => exec("redo")} label="Redo" testId="tb-redo">
            <Redo2 className="size-4" />
          </ToolButton>
        </div>

        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="editor-content prose-content"
          data-testid="blog-editor"
        />
      </Card>
    </div>
  );
}
